/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import {CTALink} from '../buttons/CTALink';

export interface FormSectionType {
  children: ReactNode;
  title?: string | ReactNode;
  description?: string | ReactNode;
  legend?: string | ReactNode;
  legendAsError?: boolean;
  bottomBorder?: boolean;
  gridSpacing?: any;
  required?: boolean;
  bottomStyle?: boolean;
  stickyTitle?: boolean;
  overrideClasses?: GenericObject;
  ctaText?: string;
  onCTAClick?: MouseEventHandler<HTMLSpanElement>;
}

const useStyles = makeStyles(theme => ({
  formSection : {
    borderBottom : ({bottomBorder}: {bottomBorder: boolean}) =>
      bottomBorder ? `solid 1px ${dlsColor('grey4')}` : 'none',
    padding : '36px 0'
  },

  formSectionContent : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center'
  },

  formTitleSection : {
    display       : 'flex',
    flexDirection : 'column'
  },

  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase'
  },

  description : {
    ...dlsAlt('typography', 'label2'),
    fontSize      : '14px',
    marginBottom  : '20px',
    marginTop     : '-8px',
    lineHeight    : '20px',
    letterSpacing : '0px',
    fontFamily    : 'Neo Sans Std'
  },

  legend : {
    ...dlsAlt('typography', 'label2'),
    color         : ({legendAsError}: {legendAsError: boolean}) => legendAsError ? dlsColor('caRed') : dlsColor('aaBlue'),
    letterSpacing : '0px',
    position      : 'relative',
    bottom        : ({bottomStyle}: {bottomStyle: boolean}) => bottomStyle ? '35px' : 0
  },

  asterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },

  stickyFormSectionContent : {
    display         : 'flex',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    position        : 'sticky',
    top             : 0,
    backgroundColor : '#f2f2f2',
    zIndex          : 99999
  },

  ctaLink : {
    ...dlsTypography('cta'),
    color          : dlsColor('aaBlue'),
    cursor         : 'pointer',
    letterSpacing  : '1px',
    lineHeight     : 'inherit',
    fontSize       : 'inherit',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }
}));

/**
 * @FormSection
 * Wrapper component for form sections
 *
 * @param {Node} children Component that is to be wrapped
 * @param {Node | string} title Section title
 * @param {Node | string} legend Section legend (optional)
 * @param {boolean} bottomBorder should the section have a border
 *   to separate it between sections (optional)
 *
 * ```typescript
 *
 * import {FormSection} from '@ion/dls/src/components/forms/FormSection';
 *
 * <FormSection bottomBorder
 *   title='Awesome Section'
 *   legend='*Required Fields'
 *   >
 *   <Component />
 * </FormSection>
 *
 * ```
 *
 */
export const FormSection = ({
  children,
  title,
  description,
  ctaText,
  onCTAClick,
  overrideClasses,
  legend = null,
  bottomBorder = false,
  legendAsError = false,
  bottomStyle = false,
  gridSpacing = 4,
  required = false,
  stickyTitle = false
}: FormSectionType) => {
  const classes = useStyles({bottomBorder, legendAsError, bottomStyle});

  const header = (
    <div className={stickyTitle
      ? classnames(classes.stickyFormSectionContent, overrideClasses?.formSectionContent)
      : classes.formSectionContent}>
      {title && (
        <h1 className={classes.formSectionTitle}>
          {title}
          {required && <span className={classes.asterisk}>*</span>}
        </h1>
      )}
      {ctaText && (
        <CTALink className={classes.ctaLink}
          onClick={onCTAClick}>
          {ctaText}
        </ CTALink>
      )}
      {legend && (
        <div className={classes.legend}>
          {legend}
        </div>
      )}
    </div>
  );

  return (
    <section className={classnames(classes.formSection, overrideClasses?.formSection)}>
      {!description ? (
        header
      ) : (
        <div className={classes.formTitleSection}>
          {header}
          <div className={classes.description}>
            {description}
          </div>
        </div>
      )}
      <Grid container
        justifyContent='space-between'
        spacing={gridSpacing}>
        {children}
      </Grid>
    </section>
  );
};

export default FormSection;
